import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SectionLoading from '@components/SectionLoading';
import { getDateTime } from '@utils/timeHelpers';
import { MentorFormActivity } from '../types';
import {
  AddButton,
  ActionButton,
  ActionsCell,
  EmptyState,
  Header,
  LoadingOverlay,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  Title,
} from '../styles';

type MentorFormsTableProps = {
  forms: MentorFormActivity[];
  currentUserId?: string;
  isTechAdmin: boolean;
  loading: boolean;
  error: string;
  onAdd: () => void;
  onView: (form: MentorFormActivity) => void;
  onEdit: (form: MentorFormActivity) => void;
  onDelete: (form: MentorFormActivity) => void;
  deletingId: string | null;
};

const formatDate = (date?: { at?: string } | string) => {
  const value = typeof date === 'string' ? date : date?.at;
  return value ? getDateTime(value) : '-';
};

const MentorFormsTable = ({
  forms,
  currentUserId,
  isTechAdmin,
  loading,
  error,
  onAdd,
  onView,
  onEdit,
  onDelete,
  deletingId,
}: MentorFormsTableProps) => (
  <>
    <Header>
      <Title>Mentor Forms</Title>
      <AddButton type="button" onClick={onAdd} disabled={loading}>
        <AddIcon fontSize="small" />
        Add new mentor form
      </AddButton>
    </Header>
    {loading && (
      <LoadingOverlay>
        <SectionLoading />
      </LoadingOverlay>
    )}
    <StyledTableContainer>
      <StyledTable stickyHeader aria-label="Mentor forms">
        <StyledTableHead>
          <StyledTableRow>
            <StyledTableCell>Form name</StyledTableCell>
            <StyledTableCell>Mentor Name</StyledTableCell>
            <StyledTableCell>Create Date</StyledTableCell>
            <StyledTableCell>Update Date</StyledTableCell>
            <ActionsCell>Actions</ActionsCell>
          </StyledTableRow>
        </StyledTableHead>
        <StyledTableBody>
          {!loading &&
            !error &&
            forms.map((form) => {
              const canManage = isTechAdmin || form.createdBy?.id === currentUserId;

              return (
                <StyledTableRow
                  key={form.id}
                  tabIndex={deletingId ? -1 : 0}
                  aria-label={`View ${form.name || 'mentor form'}`}
                  onClick={() => !deletingId && onView(form)}
                  onKeyDown={(event) => {
                    const isViewKey = event.key === 'Enter' || event.key === ' ';
                    if (event.target === event.currentTarget && !deletingId && isViewKey) {
                      event.preventDefault();
                      onView(form);
                    }
                  }}
                >
                  <StyledTableCell>{form.name || '-'}</StyledTableCell>
                  <StyledTableCell>{form.createdBy?.fullName || '-'}</StyledTableCell>
                  <StyledTableCell>{formatDate(form.createdAt)}</StyledTableCell>
                  <StyledTableCell>{formatDate(form.updatedAt)}</StyledTableCell>
                  <ActionsCell>
                    <ActionButton
                      $variant="edit"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onEdit(form);
                      }}
                      disabled={!!deletingId || !canManage}
                      aria-label={canManage ? 'Edit row' : 'Only the form owner can edit this row'}
                      title={canManage ? 'Edit row' : 'Only the form owner can edit this row'}
                      style={{ marginRight: 6 }}
                    >
                      <EditIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton
                      $variant="delete"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(form);
                      }}
                      disabled={!!deletingId || !canManage}
                      aria-label={canManage ? 'Delete row' : 'Only the form owner can delete this row'}
                      title={canManage ? 'Delete row' : 'Only the form owner can delete this row'}
                    >
                      {deletingId === form.id ? (
                        <HourglassEmptyIcon fontSize="small" />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </ActionButton>
                  </ActionsCell>
                </StyledTableRow>
              );
            })}
        </StyledTableBody>
      </StyledTable>
      {!loading && (error || !forms.length) && <EmptyState>{error || 'No mentor forms found.'}</EmptyState>}
    </StyledTableContainer>
  </>
);

export default MentorFormsTable;
